import { zodResolver } from "@hookform/resolvers/zod";
import {
  KdsButton,
  KdsCardFile,
  KdsModal,
  KdsText,
} from "@kazaar-platform/design-system";
import { RiCheckFill, RiCloseLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useState } from "react";
import {
  FormProvider,
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import {
  FileContext,
  FileContextProvider,
} from "@/components/FileUploadAsync/FileUploadAsync.context";
import { useToasts } from "@/hooks";
import { useOrderItemProofs } from "@/hooks/queries/useOrderItemProofs";
import { type ProofValidationActionsProps } from "./ProofValidationActions.types";
import { ProofValidationActionsForm } from "./ProofValidationActionsForm";
import {
  defaultValuesRefuseProof,
  type InferredRefuseProofSchema,
  refuseProofSchema,
} from "./ProofValidationActionsForm/ProofValidationActionsForm.schema";

export const ProofValidationActions = ({
  files,
  sandboxItemProofId,
  onError,
  versions,
  orderItem,
}: ProofValidationActionsProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();

  // State
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Hooks
  const { useApproveOrderItemProof, useRefuseOrderItemProof } =
    useOrderItemProofs();
  const { mutateAsync: approveSandboxItemProof, isPending: isPendingApproval } =
    useApproveOrderItemProof();
  const { mutateAsync: refuseSandboxItemProof, isPending: isPendingRefusal } =
    useRefuseOrderItemProof();

  // Define Form
  const methods = useForm<InferredRefuseProofSchema>({
    mode: "onSubmit",
    defaultValues: defaultValuesRefuseProof(versions),
    resolver: zodResolver(refuseProofSchema),
  });

  // Handle onValidate
  const handleApprove = async () => {
    const res = await approveSandboxItemProof({
      orderId: orderItem.orderId,
      orderItemId: orderItem.id,
      sandboxItemProofId: sandboxItemProofId,
      heapMetadata: { orderId: orderItem.orderId },
    });

    if (res.status === 200 || res.status === 201 || res.status === 204) {
      // Display success toast
      add({
        state: "success",
        title: t(
          "order.myOrdersItemDetails.cardAction.fileProof.toast.validate.success"
        ),
      });
    } else {
      if (import.meta.env.DEV) {
        console.error("error");
      }

      // Show Callout
      if (onError) {
        onError();
      }

      // Display error toast
      add({
        state: "error",
        title: t("toast.generic.error"),
      });
    }
  };

  const onRefuseError: SubmitErrorHandler<InferredRefuseProofSchema> = (
    error
  ) => {
    if (import.meta.env.DEV) {
      console.error("error: ", error, methods.formState);
    }
    if (onError) {
      onError();
    }
    add({ state: "error", title: t("toast.generic.error") });
  };

  // Handle onRefuse
  const onRefuse: SubmitHandler<InferredRefuseProofSchema> = async (data) => {
    const res = await refuseSandboxItemProof({
      orderId: orderItem.orderId,
      orderItemId: orderItem.id,
      sandboxItemProofId: sandboxItemProofId,
      sandboxItemProofRefuseCommand: {
        isCancelled: false,
        isValidated: false,
        refusalReason: data.refusalReason,
        versions: data.versions
          .filter((v) => !!v.fileSignedId)
          .map((v) => ({
            versionId: v.versionId,
            fileSignedId: v.fileSignedId ?? "",
          })),
      },
    });

    if (res.status === 200 || res.status === 201) {
      setIsModalOpen(!isModalOpen);
      add({
        state: "success",
        title: t(
          "order.myOrdersItemDetails.cardAction.fileProof.toast.refuse.success"
        ),
      });
    }
  };

  return (
    <>
      <div className="flex flex-col gap-sp-md rounded-2xl bg-white !p-0">
        {files.map((file) => (
          <>
            <KdsCardFile
              key={file.url ?? file.blobId}
              fileName={file.fileName}
              fileUrl={file.url}
              fileSize={file.size}
              isViewable
            />

            {!!file.comment && (
              <div className="flex flex-col gap-2 px-4">
                <KdsText size="subtitle-sm">
                  {t("order.myOrdersItemDetails.specificRequest")}
                </KdsText>
                <KdsText>{file.comment}</KdsText>
              </div>
            )}
          </>
        ))}

        <div className="flex w-full items-center justify-end gap-sp-xs">
          <KdsButton
            variant="outlined"
            color="error"
            iconLeft={RiCloseLine}
            size="small"
            onPress={() => setIsModalOpen(true)}
          >
            {t("buttons.refuse")}
          </KdsButton>

          <KdsButton
            color="success"
            size="small"
            iconLeft={RiCheckFill}
            isLoading={isPendingApproval}
            disabled={isPendingApproval}
            dataCy="validateProofButton"
            onPress={() => void handleApprove()}
          >
            {t("buttons.validateAction")}
          </KdsButton>
        </div>
      </div>

      {/* Modal */}
      <FileContextProvider>
        <FileContext.Consumer>
          {(ctx) => (
            <KdsModal
              isOpen={isModalOpen}
              onOpenChange={() => {
                if (isModalOpen) {
                  methods.clearErrors();
                }
                setIsModalOpen(!isModalOpen);
              }}
              dialogProps={{
                dialogHeaderProps: {
                  title: t(
                    "order.myOrdersItemDetails.cardAction.fileProof.modal.title"
                  ),
                },
                content: (
                  <FormProvider {...methods}>
                    <ProofValidationActionsForm />
                  </FormProvider>
                ),
                dialogFooterProps: {
                  buttonProps: [
                    {
                      children: t(
                        "order.myOrdersItemDetails.cardAction.fileProof.modal.buttonCancel"
                      ),
                      variant: "ghost",
                      color: "neutral",
                      isLoading: isPendingRefusal,
                      disabled: isPendingRefusal || ctx.isUploading,
                      onPress: () => {
                        methods.clearErrors();
                        setIsModalOpen(false);
                      },
                    },
                    {
                      children: t(
                        "order.myOrdersItemDetails.cardAction.fileProof.modal.buttonSubmit"
                      ),
                      type: "submit",
                      isLoading: isPendingRefusal,
                      disabled: isPendingRefusal || ctx.isUploading,
                      dataCy: "submitProofValidationButton",
                      onPress: () =>
                        void methods.handleSubmit(onRefuse, onRefuseError)(),
                    },
                  ],
                },
              }}
            />
          )}
        </FileContext.Consumer>
      </FileContextProvider>
    </>
  );
};
